import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUserId, getIsEsignTaskViewed, getIsLoanDetailsUpdateViewed, getLoanId, getNhDealApplicationId, setIsEsignTaskViewed } from "features/authSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { useGetNotificationDetailsQuery, useSetStatusFlagOfNotificationMutation, useSetEsignStatusFlagOfNotificationMutation, NotificationType } from "pages/authentication/userService";
import { PalNotificationProps } from "components/v2/notifications";
import { useGetLoanSummaryDetailQuery } from "pages/dashboard/home/dashboardService";
import { useUqualVisibilityFlags } from "hooks/useUqualVisibilityFlags";
import { UQUAL_NOTIFICATION_TITLE, UQUAL_NOTIFICATION_TEXT } from "utilities/Constant";

interface UseNotificationsOptions {
  enabled?: boolean;
}

const normalizeNotificationType = (type?: string): NotificationType | undefined => {
  if (!type) return undefined;

  const normalized = type.toLowerCase();
  if (normalized === "esign") return NotificationType.Esign;
  if (normalized === "pal") return NotificationType.PAL;
  if (normalized === "loandetails") return NotificationType.LoanDetails;

  return undefined;
};

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const { enabled = true } = options;
  const dispatch = useDispatch();
  const [notifications, setNotifications] = useState<PalNotificationProps[]>([]);
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanId = useSelector(getLoanId);
  const isEsignTaskViewed = useSelector(getIsEsignTaskViewed);
  const isLoanDetailsUpdateViewed = useSelector(getIsLoanDetailsUpdateViewed);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const {
    data: loanSummary,
    isLoading: isLoanSummaryLoading,
    isError: isLoanSummaryError
  } = useGetLoanSummaryDetailQuery(
    { nhDealApplicationId, loanId, persistState: true },
    { skip: !nhDealApplicationId || !loanId || !featureFlag?.loanDetailsEnabled }
  );


  const {
    data: notificationData,
    isLoading,
    error,
  } = useGetNotificationDetailsQuery(userId, {
    skip: !enabled || !featureFlag?.preApprovalLetterEnabled,
  });

  const [updateNotificationStatus] = useSetStatusFlagOfNotificationMutation();
  const [updateEsignNotificationStatus] = useSetEsignStatusFlagOfNotificationMutation();
  
  const markNotificationAsViewed = async (notification: PalNotificationProps) => {
    const isEsign = notification.notificationType === NotificationType.Esign;
    return isEsign
      ? updateEsignNotificationStatus(notification.id)
      : updateNotificationStatus(notification.id);
  };
  
  const extractPalInstanceId = (link: string): string | undefined => {
    if (link && link.length > 0 && !link.includes("/")) {
      return link;
    }
    if (link?.includes("/")) {
      try {
        const url = new URL(link, globalThis.location.origin);
        return url.searchParams.get("palInstanceId") || undefined;
      } catch {
        return undefined;
      }
    }

    return undefined;
  };

  useEffect(() => {
    if (!notificationData || !nhDealApplicationId) return;

    setNotifications(prev => {
      const map = new Map(prev.map(n => [n.id, n]));

      for (const n of notificationData) {
        if (
          n.nhDealApplicationId != null &&
          n.nhDealApplicationId !== nhDealApplicationId
        ) {
          continue;
        }

        if (n.notificationType === NotificationType.Esign && n.isEsignViewed) {
          dispatch(setIsEsignTaskViewed(true));
        }

        const normalizedType = normalizeNotificationType(n.notificationType as unknown as string | undefined);

        map.set(n.id, {
          id: n.id,
          userId: n.userId,
          nhDealApplicationId: n.nhDealApplicationId,
          icon:
            normalizedType === NotificationType.Esign
              ? "paper"
              : "folder",
          title: n.title,
          text: n.text,
          createdOn: new Date(n.createdOn),
          notificationType: normalizedType,
          palInstanceId: extractPalInstanceId(n.link),
        });
      }

      return Array.from(map.values());
    });
  }, [notificationData, nhDealApplicationId]);

  useEffect(() => {
    if (isLoanSummaryLoading || isLoanSummaryError || !loanSummary || isLoanDetailsUpdateViewed) return;

    // Check for updated loan fields and create a notification if appropriate
    if (loanSummary.updatedFields && loanSummary.updatedFields.length > 0) {
      setNotifications([
        ...notifications,
        {
          id: `loan-update-${Date.now()}`,
          title: "Loan Details Updated",
          text: "Your loan details have been updated",
          icon: "paper",
          createdOn: new Date(),
          userId,
          nhDealApplicationId: nhDealApplicationId,
          notificationType: NotificationType.LoanDetails
        }
      ])
    }
  }, [loanSummary]);

  const { showUqualNotification } = useUqualVisibilityFlags({
    skipQuery: !enabled || !nhDealApplicationId || !userId,
  });

  const uqualNotification = useMemo((): PalNotificationProps | null => {
    if (!showUqualNotification || !userId || !nhDealApplicationId) return null;
    return {
      id: "uqual-notification",
      userId,
      nhDealApplicationId,
      icon: "paper",
      title: UQUAL_NOTIFICATION_TITLE,
      text: UQUAL_NOTIFICATION_TEXT,
      createdOn: new Date(),
      notificationType: NotificationType.Uqual,
    };
  }, [showUqualNotification, userId, nhDealApplicationId]);

  const allNotifications = useMemo(() => {
    if (!uqualNotification) return notifications;
    const withoutUqual = notifications.filter((n) => n.id !== "uqual-notification");
    return [...withoutUqual, uqualNotification];
  }, [notifications, uqualNotification]);

  const removeNotification = async (notificationId: string) => {
    if (notificationId === "uqual-notification") return;
    const notification = notifications.find((n) => n.id === notificationId);
    if (!notification) return;

    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

    await markNotificationAsViewed(notification).catch(() => {});
  };

  const clearAllNotifications = async () => {
    const clearable = notifications.filter((n) => n.id !== "uqual-notification");
    if (clearable.length === 0) return;

    const updatePromises = clearable.map((notification) => markNotificationAsViewed(notification).catch(() => {}));

    setNotifications((prev) => prev.filter((n) => n.id === "uqual-notification"));
    await Promise.all(updatePromises);
  };

  return {
    notifications: allNotifications,
    isLoading,
    error,
    removeNotification,
    clearAllNotifications,
    hasNotifications: allNotifications.length > 0,
    notificationCount: allNotifications.length,
  };
};
